import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'

// localStorage keys
const STORAGE_KEYS = {
  START_TIME: 'sg_start_time',
  VISIT_COUNT: 'sg_visit_count',
  LOCKED: 'sg_locked',
  LOCK_TIMESTAMP: 'sg_lock_timestamp',
  LAST_ACTIVITY: 'sg_last_activity',
  PAUSED_TIME: 'sg_paused_time', // Time accumulated when paused
  PAUSE_START: 'sg_pause_start', // When pause started
} as const

// Thresholds
const TIME_THRESHOLD_MS = 90000 // 90 seconds
const VISIT_THRESHOLD = 4 // 4 visits
const INACTIVITY_THRESHOLD_MS = 24 * 60 * 60 * 1000 // 24 hours in milliseconds
const LOCK_DURATION_MS = 60 * 60 * 1000 // 1 hour lock duration

// Check if route is a bond detail page
const isBondRoute = (path: string): boolean => {
  return path.startsWith('/products/bonds/') && path !== '/products/bonds'
}

// Storage helpers
const getStorage = (key: string): string | null => {
  if (typeof window === 'undefined') return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const setStorage = (key: string, value: string): void => {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(key, value)
    // Note: StorageEvent is automatically fired in other tabs, not the current tab
  } catch {
    // Ignore storage errors (e.g., private browsing)
  }
}

const removeStorage = (key: string): void => {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(key)
    // Note: StorageEvent is automatically fired in other tabs, not the current tab
  } catch {
    // Ignore storage errors
  }
}

export const useSignupGate = () => {
  const route = useRoute()
  const showDialog = ref(false)
  
  let timeInterval: ReturnType<typeof setInterval> | null = null
  let currentPageStartTime: number | null = null
  let storageEventListener: ((e: StorageEvent) => void) | null = null
  let sessionStartTime: number | null = null // Track when current session started on bond page

  // Clear lock if 1-hour duration has expired
  const checkAndClearExpiredLock = (): void => {
    const locked = getStorage(STORAGE_KEYS.LOCKED)
    if (locked !== 'true') return
    const lockTs = getStorage(STORAGE_KEYS.LOCK_TIMESTAMP)
    if (!lockTs) return
    const ts = Number.parseInt(lockTs, 10)
    if (Number.isNaN(ts)) return
    if (Date.now() - ts >= LOCK_DURATION_MS) {
      removeStorage(STORAGE_KEYS.LOCKED)
      removeStorage(STORAGE_KEYS.LOCK_TIMESTAMP)
      showDialog.value = false
    }
  }

  const isLocked = computed(() => getStorage(STORAGE_KEYS.LOCKED) === 'true')

  // Get paused time accumulated
  const getPausedTime = (): number => {
    const pausedTime = getStorage(STORAGE_KEYS.PAUSED_TIME)
    if (!pausedTime) return 0
    
    const parsed = Number.parseInt(pausedTime, 10)
    return Number.isNaN(parsed) ? 0 : parsed
  }

  // Get total time spent (only counting time on bond pages)
  const getTotalTime = (): number => {
    const startTime = getStorage(STORAGE_KEYS.START_TIME)
    if (!startTime) return 0
    
    const start = Number.parseInt(startTime, 10)
    if (Number.isNaN(start)) return 0
    
    // Get accumulated paused time
    const pausedTime = getPausedTime()
    
    // If currently on bond page, add current session time
    if (isBondRoute(route.path) && sessionStartTime) {
      const currentSessionTime = Date.now() - sessionStartTime
      return pausedTime + currentSessionTime
    }
    
    // If not on bond page, return only paused time (timer is paused)
    return pausedTime
  }

  // Pause timer (when leaving bond page)
  const pauseTimer = (): void => {
    if (!isBondRoute(route.path) || !sessionStartTime) return
    
    // Calculate time spent in current session
    const sessionTime = Date.now() - sessionStartTime
    const currentPausedTime = getPausedTime()
    
    // Add current session time to paused time
    setStorage(STORAGE_KEYS.PAUSED_TIME, (currentPausedTime + sessionTime).toString())
    
    // Clear session start time
    sessionStartTime = null
  }

  // Resume timer (when entering bond page)
  const resumeTimer = (): void => {
    if (!isBondRoute(route.path)) return
    
    // Start new session
    sessionStartTime = Date.now()
    
    // Initialize start time if not set
    const existingStartTime = getStorage(STORAGE_KEYS.START_TIME)
    if (!existingStartTime) {
      setStorage(STORAGE_KEYS.START_TIME, Date.now().toString())
    }
  }

  // Get visit count
  const getVisitCount = (): number => {
    const count = getStorage(STORAGE_KEYS.VISIT_COUNT)
    if (!count) return 0
    
    const parsed = Number.parseInt(count, 10)
    return Number.isNaN(parsed) ? 0 : parsed
  }

  // Track visited routes to avoid double-counting
  const visitedRoutes = new Set<string>()

  // Update last activity timestamp
  const updateLastActivity = (): void => {
    setStorage(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString())
  }

  // Get last activity timestamp
  const getLastActivity = (): number | null => {
    const lastActivity = getStorage(STORAGE_KEYS.LAST_ACTIVITY)
    if (!lastActivity) return null
    
    const timestamp = Number.parseInt(lastActivity, 10)
    return Number.isNaN(timestamp) ? null : timestamp
  }

  // Check if user has been inactive for threshold period
  const checkInactivity = (): boolean => {
    const lastActivity = getLastActivity()
    if (!lastActivity) return false // No activity recorded yet, not inactive
    
    const timeSinceLastActivity = Date.now() - lastActivity
    return timeSinceLastActivity >= INACTIVITY_THRESHOLD_MS
  }

  // Restart 90-second timer (on refresh - fresh countdown)
  const restartTimer = (): void => {
    removeStorage(STORAGE_KEYS.PAUSED_TIME)
    removeStorage(STORAGE_KEYS.PAUSE_START)
    setStorage(STORAGE_KEYS.START_TIME, Date.now().toString())
    sessionStartTime = isBondRoute(route.path) ? Date.now() : null
  }

  // Reset all tracking data (for inactivity reset)
  const resetTrackingData = (): void => {
    removeStorage(STORAGE_KEYS.START_TIME)
    removeStorage(STORAGE_KEYS.VISIT_COUNT)
    removeStorage(STORAGE_KEYS.LOCKED)
    removeStorage(STORAGE_KEYS.LOCK_TIMESTAMP)
    removeStorage(STORAGE_KEYS.PAUSED_TIME)
    removeStorage(STORAGE_KEYS.PAUSE_START)
    visitedRoutes.clear()
    showDialog.value = false
    sessionStartTime = null
  }

  // Increment visit count for bond routes (only once per route)
  const incrementVisitCount = (path: string): void => {
    if (!isBondRoute(path)) return
    
    // Skip if already visited this route
    if (visitedRoutes.has(path)) return
    
    visitedRoutes.add(path)
    
    // Update last activity
    updateLastActivity()
    
    const currentCount = getVisitCount()
    const newCount = currentCount + 1
    setStorage(STORAGE_KEYS.VISIT_COUNT, newCount.toString())
    
    // Check if threshold reached
    if (newCount >= VISIT_THRESHOLD) {
      lockGate()
    }
  }

  // Lock the gate (1 hour duration)
  const lockGate = (): void => {
    setStorage(STORAGE_KEYS.LOCKED, 'true')
    setStorage(STORAGE_KEYS.LOCK_TIMESTAMP, Date.now().toString())
    // Only show dialog if currently on a bond details page
    if (isBondRoute(route.path)) {
      showDialog.value = true
    }
  }

  // Check if gate should be triggered
  const checkGate = (): void => {
    checkAndClearExpiredLock()

    // Only check/show dialog if on bond details page
    if (!isBondRoute(route.path)) {
      showDialog.value = false
      return
    }

    // If already locked, show dialog
    if (isLocked.value) {
      showDialog.value = true
      return
    }

    // Check time threshold (only counts time on bond pages)
    const totalTime = getTotalTime()
    if (totalTime >= TIME_THRESHOLD_MS) {
      lockGate()
      return
    }

    // Check visit count threshold
    const visitCount = getVisitCount()
    if (visitCount >= VISIT_THRESHOLD) {
      lockGate()
      return
    }
  }

  // Start tracking time on current page
  const startPageTimer = (): void => {
    if (!isBondRoute(route.path)) {
      // Not a bond route, pause timer and stop interval
      pauseTimer()
      stopPageTimer()
      return
    }

    // Update last activity
    updateLastActivity()

    // Resume timer (starts new session)
    resumeTimer()

    currentPageStartTime = Date.now()

    // Check gate immediately
    checkGate()

    // Set up interval to check gate periodically
    if (timeInterval) {
      clearInterval(timeInterval)
    }

    timeInterval = setInterval(() => {
      // Only check if still on bond route
      if (isBondRoute(route.path)) {
        checkGate()
      } else {
        // Pause if navigated away
        pauseTimer()
        stopPageTimer()
      }
    }, 1000) // Check every second
  }

  // Stop tracking time on current page
  const stopPageTimer = (): void => {
    // Pause timer before stopping
    pauseTimer()
    
    if (timeInterval) {
      clearInterval(timeInterval)
      timeInterval = null
    }
    currentPageStartTime = null
  }

  // Handle route changes
  const handleRouteChange = (newPath: string): void => {
    // Pause timer if leaving bond page
    if (isBondRoute(route.path) && !isBondRoute(newPath)) {
      pauseTimer()
    }
    
    stopPageTimer()
    
    // Check for inactivity on route change
    if (checkInactivity()) {
      resetTrackingData()
      updateLastActivity()
    }
    
    if (isBondRoute(newPath)) {
      incrementVisitCount(newPath)
      startPageTimer()
    } else {
      // Update last activity even if not a bond route
      updateLastActivity()
    }
  }

  // Handle storage key cleared (inactivity reset from another tab). Returns true if handled.
  const handleInactivityResetFromStorage = (): boolean => {
    if (!checkInactivity()) return false
    resetTrackingData()
    updateLastActivity()
    if (isBondRoute(route.path)) {
      restartTimer()
      incrementVisitCount(route.path)
      startPageTimer()
    }
    return true
  }

  // Handle LOCKED key change from another tab
  const handleLockedKeyChange = (newValue: string | null): void => {
    if (newValue === 'true' && isBondRoute(route.path)) {
      showDialog.value = true
    } else if (newValue === null) {
      showDialog.value = false
    }
  }

  // Handle storage events (cross-tab sync) - only applies when on bond details page
  const handleStorageEvent = (e: StorageEvent): void => {
    if (e.storageArea !== localStorage) return
    if (!e.key || !Object.values(STORAGE_KEYS).includes(e.key as any)) return

    if (e.newValue === null && handleInactivityResetFromStorage()) {
      return
    }

    if (e.key === STORAGE_KEYS.LOCKED) {
      handleLockedKeyChange(e.newValue)
    }

    if (isBondRoute(route.path)) {
      checkGate()
    }
  }
  
  // Store reference to event listener for cleanup
  storageEventListener = handleStorageEvent

  // Initialize the gate (bond details pages only)
  const initGate = (): void => {
    if (typeof window === 'undefined') return
    if (!isBondRoute(route.path)) return

    checkAndClearExpiredLock()

    // Check for inactivity first - reset if inactive for 24+ hours
    if (checkInactivity()) {
      resetTrackingData()
      updateLastActivity()
      if (isBondRoute(route.path)) {
        restartTimer()
        incrementVisitCount(route.path)
        startPageTimer()
      }
      // Listen for storage events (cross-tab sync)
      if (storageEventListener) {
        window.addEventListener('storage', storageEventListener)
      }
      return
    }

    // Check if already locked
    if (isLocked.value) {
      // Only show dialog if on bond details page
      if (isBondRoute(route.path)) {
        showDialog.value = true
      }
      // Still update last activity even if locked
      updateLastActivity()
      // Listen for storage events (cross-tab sync)
      if (storageEventListener) {
        window.addEventListener('storage', storageEventListener)
      }
      return
    }

    // Initialize and on bond route - restart timer on refresh (fresh 90s countdown)
    if (isBondRoute(route.path)) {
      updateLastActivity()
      restartTimer()
      incrementVisitCount(route.path)
      resumeTimer()
      startPageTimer()
    } else {
      // Even if not on bond route, update last activity
      updateLastActivity()
      // Ensure timer is paused
      pauseTimer()
    }

    // Listen for storage events (cross-tab sync)
    if (storageEventListener) {
      window.addEventListener('storage', storageEventListener)
    }

    // Check gate on init
    checkGate()
  }

  // Reset the gate (after successful signup)
  const resetGate = (): void => {
    stopPageTimer()
    
    // Clear all storage
    removeStorage(STORAGE_KEYS.START_TIME)
    removeStorage(STORAGE_KEYS.VISIT_COUNT)
    removeStorage(STORAGE_KEYS.LOCKED)
    removeStorage(STORAGE_KEYS.LOCK_TIMESTAMP)
    removeStorage(STORAGE_KEYS.LAST_ACTIVITY)
    removeStorage(STORAGE_KEYS.PAUSED_TIME)
    removeStorage(STORAGE_KEYS.PAUSE_START)
    
    // Hide dialog
    showDialog.value = false
    
    // Clear visited routes
    visitedRoutes.clear()
    
    // Reset session
    sessionStartTime = null
    
    // Remove storage event listener
    if (typeof window !== 'undefined' && storageEventListener) {
      window.removeEventListener('storage', storageEventListener)
    }
  }

  // Watch route changes
  watch(() => route.path, (newPath) => {
    handleRouteChange(newPath)
    
    // Hide dialog if navigating away from bond details page
    if (!isBondRoute(newPath)) {
      showDialog.value = false
    } else if (isLocked.value) {
      // Show dialog if locked and navigating to bond details page
      showDialog.value = true
    }
  }, { immediate: false })

  // Cleanup on unmount
  onUnmounted(() => {
    stopPageTimer()
    if (typeof window !== 'undefined' && storageEventListener) {
      window.removeEventListener('storage', storageEventListener)
    }
  })

  return {
    showDialog,
    initGate,
    resetGate,
    isLocked,
  }
}
